import React, { useState, useMemo } from 'react';
import { Filter, X } from 'lucide-react';
import { TaskCard } from './TaskCard';
import { getBranchMeta } from '../utils/format';
import styles from './TaskBoard.module.css';

const STATUSES = ['idle', 'working', 'blocked', 'escalated', 'done'];

export function TaskBoard({ tasks: tasksData, onSelectTask }) {
  const tasks = tasksData?.tasks || [];
  const branches = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.branch).filter(Boolean));
    return Array.from(ids);
  }, [tasks]);

  const [filters, setFilters] = useState({ branch: '', status: '' });

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (filters.branch && task.branch !== filters.branch) return false;
      if (filters.status && task.status !== filters.status) return false;
      return true;
    });
  }, [tasks, filters]);

  const clearFilters = () => setFilters({ branch: '', status: '' });

  return (
    <section className={styles.board} aria-label="Task board">
      <div className={styles.toolbar}>
        <div className={styles.filterGroup}>
          <Filter size={14} className={styles.filterIcon} aria-hidden="true" />
          <select
            aria-label="Filter by branch"
            value={filters.branch}
            onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}
            className={styles.select}
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch} value={branch}>{branch}</option>
            ))}
          </select>

          <select
            aria-label="Filter by status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className={styles.select}
          >
            <option value="">All Statuses</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {(filters.branch || filters.status) && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearFilters}
              aria-label="Clear filters"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        <span className={styles.count}>
          {filteredTasks.length} of {tasks.length} tasks
        </span>
      </div>

      <ul className={styles.list}>
        {filteredTasks.map((task) => (
          <li key={task.id}>
            <TaskCard task={task} branchMeta={getBranchMeta(task.branch)} onClick={onSelectTask} />
          </li>
        ))}
      </ul>

      {filteredTasks.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No tasks match</p>
          <p className={styles.emptySubtitle}>Try adjusting your filters.</p>
        </div>
      )}
    </section>
  );
}
